/**
 * Firestore live-listener controller (firestore-frontend-integration, 2026-07-20).
 *
 * Owns the Firebase mechanics only: a dedicated named app, in-memory auth
 * persistence, a SERIAL sign-out/sign-in chain that recovers after rejection,
 * generation re-checks after every await, a metadata-aware silent baseline
 * (complete only after the first server-confirmed snapshot), and bounded
 * in-tab deduplication.
 *
 * What a document MEANS is the consumer's business — see `validate` in
 * `StartLiveListenerArgs`.
 */
import { FIREBASE_CONFIG } from "./config";
import { FirebaseLiveError } from "./errors";
import type { FirebaseWebConfig, LiveListenerHandle, StartLiveListenerArgs } from "../types/live";

/** Bound the in-tab dedup set so a long-lived session cannot grow it unboundedly. */
const MAX_HANDLED_DOCUMENT_IDS = 5_000;

/* ------------------------------------------------------------------ *
 * Serial auth chain — ALL signOut/signInWithCustomToken transitions run
 * through this single chain so concurrent scope changes cannot interleave.
 * The chain recovers after every rejection (`.catch(() => undefined)`) so one
 * failed sign-in can never permanently block later transitions.
 * ------------------------------------------------------------------ */
let authChain: Promise<unknown> = Promise.resolve();

function serializeAuth<T>(op: () => Promise<T>): Promise<T> {
  const run = authChain.then(op);
  authChain = run.catch(() => undefined);
  return run;
}

/* ------------------------------------------------------------------ *
 * Serial START chain — a whole start(), not just its auth transition.
 *
 * Serializing the auth transition alone is not enough, and the gap it leaves is
 * a real bug rather than a theoretical one. `signInWithCustomToken` and the
 * `onSnapshot` attach are separated by the rest of this function, and the auth
 * chain is RELEASED between them. A second scope starting in that window signs
 * the shared session in as ITSELF, and the first start then attaches to its own
 * collection while authenticated for the other one — which Firestore answers
 * with `permission-denied`, taking down a channel whose credentials were
 * perfectly valid.
 *
 * Holding the start chain across sign-in AND attach makes the pair atomic with
 * respect to other scopes. Scope changes still interleave freely; they just take
 * turns. Separate from `authChain` so `stop()`'s sign-out — which runs on the
 * auth chain from inside a start — can never wait on the chain it is part of.
 * ------------------------------------------------------------------ */
let startChain: Promise<unknown> = Promise.resolve();

function serializeStart<T>(op: () => Promise<T>): Promise<T> {
  const run = startChain.then(op);
  startChain = run.catch(() => undefined);
  return run;
}

type FirebaseAppLike = { name: string; options: { projectId?: string } };

async function getLiveApp(
  appName: string,
  expectedProjectId: string,
): Promise<FirebaseAppLike> {
  const { getApps, initializeApp } = await import("firebase/app");
  const existing = (getApps() as FirebaseAppLike[]).find(
    (candidate: FirebaseAppLike) => candidate.name === appName,
  );
  const app = existing ?? initializeApp(FIREBASE_CONFIG as FirebaseWebConfig, appName);
  if (app.options.projectId !== expectedProjectId) {
    throw new FirebaseLiveError(
      "firebase_project_mismatch",
      "Firebase web config does not match the live subscription project.",
    );
  }
  return app as FirebaseAppLike;
}

/**
 * Start (or replace) a live listener on one Firestore collection.
 *
 * Returns a handle whose `stop()` unsubscribes and signs the live session out.
 * Returns `null` if the transition was superseded (`isStale()`) or cancelled
 * before the listener attached — in that case any completed sign-in is signed
 * back out and no listener is attached.
 */
export function startLiveCollectionListener<TEvent>(
  args: StartLiveListenerArgs<TEvent>,
): Promise<LiveListenerHandle | null> {
  // One start at a time — see the start chain above. Without this, two scopes
  // resolving close together leave one listener attached under the other's
  // identity.
  return serializeStart(() => startListenerExclusively(args));
}

async function startListenerExclusively<TEvent>(
  args: StartLiveListenerArgs<TEvent>,
): Promise<LiveListenerHandle | null> {
  if (!FIREBASE_CONFIG) {
    throw new FirebaseLiveError("firebase_not_configured", "Firebase is not configured.");
  }

  const app = await getLiveApp(args.appName, args.expectedProjectId);
  if (args.isStale()) return null;

  const { getAuth, inMemoryPersistence, setPersistence, signOut, signInWithCustomToken } =
    await import("firebase/auth");
  if (args.isStale()) return null;

  const { collection, getFirestore, onSnapshot } = await import("firebase/firestore");
  if (args.isStale()) return null;

  const auth = getAuth(app as never);
  const firestore = getFirestore(app as never);

  // Project check happens before sign-in so a misconfigured frontend never
  // authenticates against the wrong project.
  if (app.options.projectId !== args.expectedProjectId) {
    throw new FirebaseLiveError(
      "firebase_project_mismatch",
      "Initialized Firebase app does not match the live subscription project.",
    );
  }

  // Serialize the full persistence → signOut → signIn transition.
  await serializeAuth(async () => {
    await setPersistence(auth, inMemoryPersistence);
    await signOut(auth);
    await signInWithCustomToken(auth, args.customToken);
  });

  // A stale transition that completed sign-in must sign out and NOT attach.
  if (args.isStale()) {
    void serializeAuth(() => signOut(auth));
    return null;
  }

  let stopped = false;
  let serverBaselineComplete = false;
  const handledDocumentIds = new Set<string>();

  const remember = (id: string) => {
    handledDocumentIds.add(id);
    if (handledDocumentIds.size > MAX_HANDLED_DOCUMENT_IDS) {
      // Drop the oldest ~20% to keep the set bounded across a long session.
      const drop = Math.floor(MAX_HANDLED_DOCUMENT_IDS * 0.2);
      let removed = 0;
      for (const key of handledDocumentIds) {
        handledDocumentIds.delete(key);
        if (++removed >= drop) break;
      }
    }
  };

  const events = collection(firestore, args.collectionPath);
  const unsubscribe = onSnapshot(
    events,
    { includeMetadataChanges: true },
    (snapshot: {
      docs: Array<{ id: string }>;
      metadata: { fromCache: boolean };
      docChanges: () => Array<{ type: string; doc: { id: string; data: () => unknown } }>;
    }) => {
      if (stopped) return;

      // Silent baseline: every currently-matching doc arrives as `added`. A
      // cached snapshot is still baseline; the baseline is only COMPLETE after
      // the first server-confirmed (non-cache) snapshot.
      if (!serverBaselineComplete) {
        for (const document of snapshot.docs) remember(document.id);
        if (!snapshot.metadata.fromCache) {
          serverBaselineComplete = true;
          args.onConnectionState("connected");
          args.onBaselineComplete();
        } else {
          args.onConnectionState("connecting");
        }
        return;
      }

      // Post-baseline cache-only snapshots mean we lost the server connection.
      if (snapshot.metadata.fromCache) {
        args.onConnectionState("degraded");
        return;
      }
      args.onConnectionState("connected");

      for (const change of snapshot.docChanges()) {
        // Only `added` documents are hints. `modified` = idempotent producer
        // retry; `removed` = TTL expiry. Neither triggers consumer workflow.
        if (change.type !== "added") continue;
        if (handledDocumentIds.has(change.doc.id)) continue;

        const event = args.validate(change.doc.id, change.doc.data());
        if (event === null) continue;

        remember(change.doc.id);
        args.onHint(event);
      }
    },
    (error: Error) => {
      if (stopped) return;
      args.onError(error);
    },
  );

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      try {
        unsubscribe();
      } catch {
        // ignore
      }
      // Terminate the Firebase session (logout / scope change / teardown).
      void serializeAuth(() => signOut(auth)).catch(() => undefined);
    },
  };
}
